import type { ReactNode } from "react";
import { t } from "../i18n";
import { AuthorCommands } from "./CommandLine";

// Numbered steps, each its title, what it does and its commands to copy: how
// an exam is set and sat (components/ExamGuide, byj-1wv.51.12), and how a
// machine is used and run (pages/Machines, byj-1wv.51.15), whose steps are
// down a rail and carry a note; and steps worked in rather than read, the
// doing of each inside it (the agent page's guides, byj-1wv.115.48), drawn
// with the same Step, beside the same Tips.

/** One way of doing a step, beside the others: any of them, or several. */
export type StepChoice = { title: string; text: string; commands?: string[]; note?: string };

export type GuideStep = { title: string; text: string; commands?: string[]; note?: string; choices?: StepChoice[] };

/**
 * Steps in order, each its title, what it does, its commands — folded under
 * it, given `fold` — its choices side by side, each a way of doing it, and
 * its note, muted, under them. `rail`, each step's number is a badge down a
 * rail, what it does in the text's colour, for steps that are followed one
 * after another rather than read through.
 */
export function Steps({ steps, testid, fold = false, rail = false }: { steps: GuideStep[]; testid?: string; fold?: boolean; rail?: boolean }) {
  return (
    <ol className={rail ? "steps steps-rail" : "steps"} data-testid={testid}>
      {steps.map((s) => (
        <Step key={s.title} title={s.title} text={s.text} note={s.note}>
          {s.commands &&
            (fold ? (
              <details className="step-commands">
                <summary className="small">{t("exams:stepCommands", { count: s.commands.length })}</summary>
                <AuthorCommands commands={s.commands} />
              </details>
            ) : (
              <AuthorCommands commands={s.commands} />
            ))}
          {s.choices && (
            <ul className="step-choices">
              {s.choices.map((c) => (
                <li key={c.title} className="step-choice">
                  <strong className="small">{c.title}</strong>
                  <p className="small flush step-text">{c.text}</p>
                  {c.commands && <AuthorCommands commands={c.commands} />}
                  {c.note && <p className="muted small flush step-note">{c.note}</p>}
                </li>
              ))}
            </ul>
          )}
        </Step>
      ))}
    </ol>
  );
}

/**
 * One step, inside an `ol.steps` (a rail's, given `steps-rail`): its title
 * with what sits beside it (`extra`: a count, a tag, what acts on it at the
 * line's end in `.row-actions`), what it does, the doing of it as children,
 * and its note, muted, under them. `done`, what it asks is so: a rail draws
 * its number as a ✓, and a screen reader is told.
 */
export function Step({ title, extra, text, note, done = false, id, testid, className, children }: {
  title: ReactNode;
  extra?: ReactNode;
  text: ReactNode;
  note?: ReactNode;
  done?: boolean;
  id?: string;
  testid?: string;
  className?: string;
  children?: ReactNode;
}) {
  const cls = [className, done ? "is-done" : ""].filter(Boolean).join(" ");
  return (
    <li id={id} data-testid={testid} className={cls || undefined}>
      {done && <span className="sr-only">{t("common:stepDone")}</span>}
      <div className="step-head">
        <strong>{title}</strong>
        {extra}
      </div>
      <p className="muted small flush step-text">{text}</p>
      {children}
      {note && <p className="muted small flush step-note">{note}</p>}
    </li>
  );
}

/** What is good to know beside steps, quieter than they are and set apart: its title, then each a line (`li`). */
export function Tips({ title, className, children }: { title: string; className?: string; children: ReactNode }) {
  return (
    <aside className={className ? `machine-tips ${className}` : "machine-tips"} aria-label={title}>
      <strong className="small">{title}</strong>
      <ul className="small muted">{children}</ul>
    </aside>
  );
}
